/**
 * Shared scheduling constants for monitor configuration.
 * Used by NewMonitorWizard, MonitorSetupModal, and MonitorDetailModal.
 */

import type { DayOfWeek } from '../types/monitoring';

/**
 * Available timezone options for monitor scheduling.
 */
export const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Etc/UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
] as const;

/**
 * Days of the week for weekly schedule configuration.
 */
export const DAYS_OF_WEEK: ReadonlyArray<{ value: DayOfWeek; label: string }> = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

/**
 * Timezone options for campaign scheduling.
 * Includes Singapore timezone for broader coverage.
 */
export const CAMPAIGN_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe (CET)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
] as const;

/**
 * Days of the week for campaign scheduling (numeric values for API).
 * Note: 0 = Sunday, 1 = Monday, etc. (JavaScript Date convention)
 */
export const CAMPAIGN_DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
] as const;

/**
 * Max results options for quick selection.
 */
export const MAX_RESULTS_OPTIONS = [10, 25] as const;

/**
 * Maximum allowed results per monitor run.
 */
export const MAX_RESULTS_LIMIT = 200;

/**
 * Default schedule configuration for new monitors.
 */
export const DEFAULT_SCHEDULE = {
  frequency: 'daily' as const,
  hourOfDay: 8,
  daysOfWeek: ['mon'] as DayOfWeek[],
  timezone: 'America/New_York',
};

/**
 * Default campaign configuration for quick send (when scheduling is disabled).
 * These are placeholder values required by the API but not used for immediate sends.
 */
export const DEFAULT_CAMPAIGN_CONFIG = {
  timezone: 'America/New_York',
  sendWindowStart: '09:00',
  sendWindowEnd: '17:00',
  sendDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const,
  dailyLimit: 50,
} as const;
